import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { InvoiceLinks } from './InvoicesSection';
import { formatCents, formatUtcDate, invoiceStatusLabel } from './money';
import { useResource } from './useResource';
import '../billing.css';

/** `/settings/billing/invoices/:invoiceId` — one invoice with its lines. Tax lines are always listed separately from
 * the charges they apply to, as the server returns them (kind `Tax`). Reachable in every subscription state. */
export function InvoiceDetailPage() {
  const { invoiceId = '' } = useParams<{ invoiceId: string }>();
  const load = useCallback((signal: AbortSignal) => subscriptionGateway.getInvoice(invoiceId, signal), [invoiceId]);
  const { state, reload } = useResource(load, 'Could not load this invoice.');

  return (
    <section className="card card--wide" aria-labelledby="invoice-heading">
      <p><Link to="/settings/billing">← Back to billing</Link></p>

      {state.status === 'loading' && <p className="muted" role="status">Loading invoice…</p>}
      {state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Billing: View" />}
      {state.status === 'unavailable' && <p className="muted">Billing is not available in this environment.</p>}
      {state.status === 'error' && <RetryableError message={state.message} onRetry={reload} />}

      {state.status === 'ready' && (() => {
        const invoice = state.data;
        const charges = invoice.lines.filter((line) => line.kind !== 'Tax');
        const taxes = invoice.lines.filter((line) => line.kind === 'Tax');
        return (
          <>
            <h2 id="invoice-heading">Invoice {invoice.number ?? ''}</h2>
            <dl className="billing-facts">
              <div><dt>Status</dt><dd>{invoiceStatusLabel(invoice.status)}</dd></div>
              <div><dt>Issued</dt><dd>{formatUtcDate(invoice.issuedAtUtc)}</dd></div>
              {invoice.periodStartUtc && invoice.periodEndUtc && (
                <div><dt>Billing period</dt><dd>{formatUtcDate(invoice.periodStartUtc)} to {formatUtcDate(invoice.periodEndUtc)}</dd></div>
              )}
              {invoice.paidAtUtc && <div><dt>Paid</dt><dd>{formatUtcDate(invoice.paidAtUtc)}</dd></div>}
            </dl>

            <table className="billing-table" aria-label="Invoice lines">
              <thead>
                <tr><th scope="col">Description</th><th scope="col" className="billing-table__amount">Amount</th></tr>
              </thead>
              <tbody>
                {charges.map((line, index) => (
                  <tr key={`charge-${index}`}>
                    <th scope="row">{line.description}</th>
                    <td className="billing-table__amount">{formatCents(line.amountCents)}</td>
                  </tr>
                ))}
                <tr className="billing-table__subtotal"><th scope="row">Subtotal</th><td className="billing-table__amount">{formatCents(invoice.subtotalCents)}</td></tr>
                {taxes.length === 0 && (
                  <tr data-testid="invoice-tax-line"><th scope="row">Tax</th><td className="billing-table__amount">{formatCents(invoice.taxCents)}</td></tr>
                )}
                {taxes.map((line, index) => (
                  <tr key={`tax-${index}`} data-testid="invoice-tax-line">
                    <th scope="row">{line.description}</th>
                    <td className="billing-table__amount">{formatCents(line.amountCents)}</td>
                  </tr>
                ))}
                <tr className="billing-table__total"><th scope="row">Total</th><td className="billing-table__amount">{formatCents(invoice.totalCents)}</td></tr>
                <tr><th scope="row">Amount paid</th><td className="billing-table__amount">{formatCents(invoice.amountPaidCents)}</td></tr>
                {invoice.amountRefundedCents > 0 && (
                  <tr><th scope="row">Refunded</th><td className="billing-table__amount">{formatCents(invoice.amountRefundedCents)}</td></tr>
                )}
              </tbody>
            </table>

            <p><InvoiceLinks hostedInvoiceUrl={invoice.hostedInvoiceUrl} invoicePdfUrl={invoice.invoicePdfUrl} label={invoice.number ?? 'this invoice'} /></p>
            <p className="muted">Questions about this invoice? Use billing support on the <Link to="/settings/billing">billing page</Link>, or email info@escochecks.com.</p>
          </>
        );
      })()}
    </section>
  );
}
